import type { Product } from '../types/inventory';

type StockFields = Pick<Product, 'currentStock' | 'stockBySite'>;

/**
 * True si `stockBySite` contient au moins une quantité > 0.
 * Une map entièrement à 0 (produit créé multi-lieux puis stocké via currentStock seul)
 * est traitée comme « pas d’allocation réelle » — on retombe sur currentStock.
 */
export const hasAllocatedSiteStock = (
  product: Pick<Product, 'stockBySite'> | null | undefined
): boolean => {
  const bySite = product?.stockBySite;
  if (!bySite || Object.keys(bySite).length === 0) return false;
  return Object.values(bySite).some((qty) => (Number(qty) || 0) > 0);
};

/**
 * Stock disponible d’un produit sur un lieu.
 * - Allocation réelle par lieu → quantité de ce lieu (0 si absent).
 * - Sinon (legacy / map vide ou tout à 0) → `currentStock`.
 */
export const getProductStockAtSite = (
  product: StockFields | null | undefined,
  siteId?: string | null
): number => {
  if (!product) return 0;
  const global = Math.max(0, Number(product.currentStock) || 0);
  if (!hasAllocatedSiteStock(product)) {
    return global;
  }
  if (!siteId) {
    return global;
  }
  return Math.max(0, Number(product.stockBySite?.[siteId] ?? 0) || 0);
};

/** True si le produit a du stock vendable sur au moins un des lieux (ou en global). */
export const productHasSellableStock = (
  product: Pick<Product, 'isActive' | 'currentStock' | 'stockBySite'> | null | undefined,
  siteIds?: string[]
): boolean => {
  if (!product || product.isActive === false) return false;
  if (siteIds && siteIds.length > 0) {
    return siteIds.some((id) => getProductStockAtSite(product, id) > 0);
  }
  if (hasAllocatedSiteStock(product)) {
    return Object.values(product.stockBySite || {}).some((qty) => (Number(qty) || 0) > 0);
  }
  return (Number(product.currentStock) || 0) > 0;
};
